import React, { useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { Plus, Edit2 } from 'lucide-react';
import moment from 'moment';
import UpdateTaxProfileModal from './UpdateTaxProfileModal';
import { Section, Facts, Fact, UpcomingBadge, HistoryToggle, HistoryTable, SectionEmpty, formatDate, formatPeriod } from './EmployeeDetailParts';

const TaxProfileCard = ({ employeeId, companyId, currentTaxProfile, history, currentContract, onUpdate }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

  const handleCreateClick = () => {
    setModalMode('create');
    setShowUpdateModal(true);
  };

  const handleEditClick = () => {
    setModalMode('edit');
    setShowUpdateModal(true);
  };

  const formatDate = (date) => {
    return moment(date).format('DD MMM YYYY');
  };

  const getTaxTypeName = (taxType) => {
    switch (taxType) {
      case 'A_SKATT':
        return 'A-skatt';
      case 'SINK':
        return 'SINK-skatt';
      default:
        return taxType;
    }
  };

  const handleUpdateSuccess = () => {
    setShowUpdateModal(false);
    if (onUpdate) onUpdate();
  };

  return (
    <>
      <Section
        id="tax-profile-heading"
        title="Skatteprofil"
        badge={<UpcomingBadge show={Boolean(currentTaxProfile?._isFuture)} />}
        meta={currentTaxProfile
          ? `${currentTaxProfile._isFuture ? 'Börjar' : 'Gäller från'} ${formatDate(currentTaxProfile.valid_from)}`
          : 'Ingen skatteprofil registrerad'}
        actions={currentTaxProfile && (
          <>
            <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" onClick={handleEditClick} aria-label="Redigera skatteprofil">
              <Edit2 />
              Ändra
            </button>
            <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={handleCreateClick} aria-label="Skapa ny skatteprofil för anställd">
              <Plus />
              Ny profil
            </button>
          </>
        )}
      >
        {currentTaxProfile ? (
          <>
            <Facts tight>
              <Fact label="Skattetyp" wide>{getTaxTypeName(currentTaxProfile.tax_type)}</Fact>
              <Fact label="Skattetabell">{currentTaxProfile.tax_table}</Fact>
              <Fact label="Kolumn">{currentTaxProfile.tax_column}</Fact>
              {currentTaxProfile.municipality_code && <Fact label="Kommunkod">{currentTaxProfile.municipality_code}</Fact>}
            </Facts>

            <HistoryToggle
              open={showHistory}
              onToggle={() => setShowHistory(!showHistory)}
              count={history && history.length > 1 ? history.length - 1 : 0}
              noun="skattehistorik"
            />
            <Collapse in={showHistory}>
              <div>
                {history && history.length > 1 && (
                  <HistoryTable
                    columns={['Period', 'Typ', 'Tabell / kolumn']}
                    rows={history}
                    rowKey="tax_id"
                    render={(p) => [formatPeriod(p), getTaxTypeName(p.tax_type), `${p.tax_table ?? '–'} / ${p.tax_column ?? '–'}`]}
                  />
                )}
              </div>
            </Collapse>
          </>
        ) : (
          <SectionEmpty
            action={
              <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={handleCreateClick} aria-label="Lägg till skatteprofil för anställd">
                <Plus />
                Lägg till skatteprofil
              </button>
            }
          >
            Utan skatteprofil dras skatt enligt tabell 33, kolumn 1 vid lönekörning.
          </SectionEmpty>
        )}
      </Section>

      <UpdateTaxProfileModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        employeeId={employeeId}
        companyId={companyId}
        currentTaxProfile={modalMode === 'edit' ? currentTaxProfile : null}
        currentContract={currentContract}
        mode={modalMode}
        onSuccess={handleUpdateSuccess}
      />
    </>
  );
};

export default TaxProfileCard;
